import _regeneratorRuntime from "@babel/runtime/regenerator";
import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { Integrations as ApmIntegrations } from "@sentry/apm";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import { positions, Provider as AlertProvider } from "react-alert";
import { render } from "react-dom";
import TagManager from "react-gtm-module";
import { hot } from "react-hot-loader";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { NotificationTemplate } from "@components/atoms";
import { ServiceWorkerProvider } from "@components/containers";
import { SaleorProvider } from "@saleor/sdk";
import { defaultTheme, GlobalStyle } from "@styles";
import Helmet from "react-helmet";
import favIcon16 from "./images/favicon/EasyPedidoIcon16x16.png";
import faviconCustomer from "./images/favicon.jpg";
import faviconDeliveryman from "./images/favicondeliveryman.jpg";
import { App } from "./app";
import { LocaleProvider } from "./components/Locale";
import { apiUrl, sentryDsn, sentrySampleRate, serviceWorkerTimeout } from "./constants";
import { ChangeAmountProductsProvider } from "./contexts";
var SALEOR_CONFIG = {
  apiUrl: apiUrl
};
var tempLocalStorageKeys = ["csrf_token", "apollo-cache-persist", "loglevel:webpack-dev-server", "auth_key", "auth_key_with_time", "token", "webAccess", "order_token", "product_url", "home_url", "mainTrade", "address-user-id", "address-from", "address-data", "address-neighborhood", "address-name-options", "address-validate-edit", "address-undefined", "address-id", "sale_cache", "category_products_cache", "subcategory_products_cache", "oneSubcategory", "vourcherListSeen", "products_cache", "scroll-home", "scroll-categories", "scroll-category", "scroll-sale", "scroll_order_list", "scroll-product", "status_page", "other-product", "home_search_clean", "sale_search_clean", "loadingLoadMoreSubCategory", "loadingLoadMoreSaleCategory", "fromProductsToNeighborhoods"]; // Prueba para que despliegue

if (process.env.GTM_ID !== undefined) {
  TagManager.initialize({
    gtmId: process.env.GTM_ID
  });
} // ------------------ Remove zoom ------------------------


document.addEventListener("gesturestart", function (e) {
  e.preventDefault(); // special hack to prevent zoom-to-tabs gesture in safari

  document.body.style.zoom = "1";
});
document.addEventListener("gesturechange", function (e) {
  e.preventDefault(); // special hack to prevent zoom-to-tabs gesture in safari

  document.body.style.zoom = "1";
});
document.addEventListener("gestureend", function (e) {
  e.preventDefault(); // special hack to prevent zoom-to-tabs gesture in safari

  document.body.style.zoom = "1";
}); // ----------------------------------------------------------

var startApp = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var notificationOptions, Root;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (sentryDsn !== undefined) {
              Sentry.init({
                dsn: sentryDsn,
                integrations: [new ApmIntegrations.Tracing()],
                tracesSampleRate: sentrySampleRate
              });
            }

            notificationOptions = {
              position: positions.BOTTOM_RIGHT,
              timeout: 2500,
              containerStyle: {
                zIndex: 999
              }
            };
            Root = hot(module)(function () {
              return React.createElement(BrowserRouter, null, React.createElement(Helmet, null, React.createElement("link", {
                rel: "apple-touch-icon",
                type: "image/jpg",
                sizes: "180x180",
                href: window.location.pathname.includes("deliveryman") ? faviconDeliveryman : faviconCustomer
              }), React.createElement("link", {
                rel: "icon",
                type: "image/jpg",
                sizes: "180x180",
                href: window.location.pathname.includes("deliveryman") ? faviconDeliveryman : faviconCustomer
              }), React.createElement("link", {
                rel: "icon",
                sizes: "16x16",
                href: window.location.pathname.includes("deliveryman") ? faviconDeliveryman : favIcon16
              })), React.createElement(SaleorProvider, {
                config: SALEOR_CONFIG
              }, React.createElement(App, null)));
            });
            render(React.createElement(ChangeAmountProductsProvider, null, React.createElement(ThemeProvider, {
              theme: defaultTheme
            }, React.createElement(AlertProvider, _extends({
              template: NotificationTemplate
            }, notificationOptions), React.createElement(ServiceWorkerProvider, {
              timeout: serviceWorkerTimeout
            }, React.createElement(LocaleProvider, null, React.createElement(GlobalStyle, null), React.createElement(Root, null)))))), document.getElementById("root")); // Hot Module Replacement API

            if (module.hot) {
              module.hot.accept();
            }

          case 5:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function startApp() {
    return _ref.apply(this, arguments);
  };
}();

var ecommerceLoginRex = new RegExp("^/web/.+/$");

var verifyActiveWindow = function verifyActiveWindow() {
  if (ecommerceLoginRex.test(window.location.pathname)) {
    return;
  }

  if (window.authKeyWithTime && window.authKeyWithTime !== window.localStorage.getItem("auth_key_with_time")) {
    location.reload();
  } else if (!window.authKeyWithTime && window.localStorage.getItem("auth_key")) {
    window.authKeyWithTime = "".concat(window.localStorage.getItem("auth_key"), "-").concat(new Date().getTime());
    window.localStorage.setItem("auth_key_with_time", window.authKeyWithTime);
  }
};

window.removeStorage = function () {
  tempLocalStorageKeys.forEach(function (key) {
    window.localStorage.removeItem(key);
  });
}; // ----------- Remove Local Storage Data ----------------


var customerLoginRex = new RegExp("^/web-access/.+/.+/.+/$");
var customerProfileRex = new RegExp("^/web-access-customer-profile/.+/.+/$");
var customerOrdersRex = new RegExp("^/web-access-orders/.+/.+/$");
var customerOrdersInProgressRex = new RegExp("^/web-access-orders-in-progress/.+/.+/$");
var customerOrderRex = new RegExp("^/web-access/.+/.+/.+/order/.+/$");
var deliveryLoginRex = new RegExp("^/delivery/web-access/.+/.+/$");
var deliverOrderRex = new RegExp("^/delivery/web-access/.+/.+/order/.+/$");
/**
 * Validates if the url is to signin with the same credentials of the last session registered in front storage
 * @returns [Boolean]
 */

(function (_customerLoginRex, _deliveryLoginRex, _customerOrdersInProgressRex, _customerOrderRex, _deliverOrderRex, _customerProfileRex, _customerOrdersRex) {
  window.isAccesingToTheCurrentSession = function () {
    // ---- Customer -------
    if (_customerLoginRex.test(window.location.pathname)) {
      var params = window.location.pathname.match(_customerLoginRex)[0].split("/");
      return localStorage.getItem("webAccess") === "".concat(params[2], "-").concat(params[4]);
    }

    if (_customerProfileRex.test(window.location.pathname)) {
      var _params = window.location.pathname.match(_customerProfileRex)[0].split("/");

      return localStorage.getItem("webAccess") === "".concat(_params[2], "-USER");
    }

    if (_customerOrdersRex.test(window.location.pathname)) {
      var _params2 = window.location.pathname.match(_customerOrdersRex)[0].split("/");

      return localStorage.getItem("webAccess") === "".concat(_params2[2], "-USER");
    }

    if (_customerOrdersInProgressRex.test(window.location.pathname)) {
      var _params3 = window.location.pathname.match(_customerOrdersInProgressRex)[0].split("/");

      return localStorage.getItem("webAccess") === "".concat(_params3[2], "-USER");
    }

    if (_customerOrderRex.test(window.location.pathname)) {
      var _params4 = window.location.pathname.match(_customerOrderRex)[0].split("/");

      return localStorage.getItem("webAccess") === "".concat(_params4[2], "-").concat(_params4[4]);
    } // ------- Delivery ---------


    if (_deliveryLoginRex.test(window.location.pathname)) {
      var _params5 = window.location.pathname.match(_deliveryLoginRex)[0].split("/");

      return localStorage.getItem("webAccess") === "".concat(_params5[3], "-DELIVERY");
    }

    if (_deliverOrderRex.test(window.location.pathname)) {
      var _params6 = window.location.pathname.match(_deliverOrderRex)[0].split("/");

      return localStorage.getItem("webAccess") === "".concat(_params6[3], "-DELIVERY");
    }

    return false;
  };
})(customerLoginRex, deliveryLoginRex, customerOrdersInProgressRex, customerOrderRex, deliverOrderRex, customerProfileRex, customerOrdersRex);
/**
 * Validates if is it allowed executes the mutation to refresh signInToken
 * @returns [Boolean]
 */


window.mustAvoidRefreshSignInToken = function () {
  return localStorage.getItem("avoidRefreshSignInToken") === "1";
};
/**
 * Marks the refresh signinToken as not allowed
 * @returns [Void]
 */


window.avoidRefreshSignInToken = function () {
  localStorage.setItem("avoidRefreshSignInToken", "1");
};
/**
 * Marks the refresh signinToken as allowed
 * @returns [Void]
 */


window.allowRefreshSignInToken = function () {
  localStorage.setItem("avoidRefreshSignInToken", "0");
};

if ((customerLoginRex.test(window.location.pathname) || customerProfileRex.test(window.location.pathname) || customerOrdersRex.test(window.location.pathname) || customerOrderRex.test(window.location.pathname) || customerOrdersInProgressRex.test(window.location.pathname) || deliveryLoginRex.test(window.location.pathname) || deliverOrderRex.test(window.location.pathname)) && !window.isAccesingToTheCurrentSession()) {
  window.removeStorage();
} else {
  verifyActiveWindow();
} // ------------------------------------------------------


window.allowRefreshSignInToken();
startApp();
document.addEventListener("visibilitychange", function () {
  if (document.visibilityState === "visible") {
    verifyActiveWindow();
  }
});
window.addEventListener("focus", function () {
  verifyActiveWindow();
});